import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { useContext, useState } from 'react';
import useSWR from 'swr';

import { UserContext } from '~/auth/userContext';
import {
  avatarColor,
  CATEGORY_LABEL,
  formatDate,
  initials,
  isoDaysFromToday,
  QUICK_FOLLOW_UPS,
  type TaskCategory,
} from '~/components/tasks/taskStyle';
import { propertiesFormList } from '~/data/generated/endpoints/properties/properties';
import type { PatchedTaskUpdateRequest, PropertyForm, TaskDetail } from '~/data/generated/model';
import { tidyNotes, useCreateTask, useTaskAssignees } from '~/data/hooks/useTasks';
import { usePropertyCars } from '~/hooks/usePropertyCars';
import { BUTTON_SX } from '~/theme';

type CreateProps = {
  open: boolean;
  propertyId?: string;
  propertyName?: string;
  portfolioName?: string | null;
  onClose: () => void;
  onCreated: (task: TaskDetail) => void;
  task?: undefined;
};

type EditProps = {
  open: boolean;
  task: TaskDetail;
  onClose: () => void;
  onSave: (changes: PatchedTaskUpdateRequest) => Promise<unknown>;
};

type Props = CreateProps | EditProps;

const choiceSx = (on: boolean) => ({
  fontSize: 13,
  border: '1px solid',
  borderColor: on ? 'primary.main' : 'divider',
  bgcolor: on ? 'primary.light' : 'background.paper',
  color: on ? 'primary.main' : 'text.primary',
  '&&:hover': { bgcolor: on ? 'primary.light' : 'action.hover' },
});

const labelSx = { fontSize: 13, fontWeight: 500 };
const hintSx = { fontSize: 12, color: 'text.secondary' };

function FieldLabel({ label, hint }: { label: string; hint?: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mb: '6px' }}>
      <Box component="span" sx={labelSx}>
        {label}
      </Box>
      {hint ? (
        <Box component="span" sx={hintSx}>
          {hint}
        </Box>
      ) : null}
    </Box>
  );
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

function usePropertyOptions(enabled: boolean) {
  const { data } = useSWR(enabled ? 'task-property-options' : null, async () => {
    const { data: properties } = await propertiesFormList();
    return properties.filter((p): p is PropertyForm & { id: string } => Boolean(p.id));
  });
  return data ?? [];
}

function NewTaskForm(props: Omit<CreateProps, 'open'> | Omit<EditProps, 'open'>) {
  const { onClose, task } = props;
  const fixedPropertyId = task ? task.propertyId : props.propertyId;
  const propertyOptions = usePropertyOptions(!fixedPropertyId);
  const [pickedProperty, setPickedProperty] = useState<{ id: string; name: string } | null>(null);
  const propertyId = fixedPropertyId ?? pickedProperty?.id ?? '';
  const propertyName = task
    ? task.propertyName
    : (props.propertyName ?? pickedProperty?.name ?? '');
  const portfolioName = task ? (task.portfolio?.name ?? null) : (props.portfolioName ?? null);
  const { userDetails } = useContext(UserContext);
  const { cars } = usePropertyCars(propertyId || null);
  const { people } = useTaskAssignees(propertyId);
  const createTask = useCreateTask();

  const initialCarIds = task ? task.cars.map((car) => car.id) : [];
  const initialAssigneeId = task ? (task.assignee?.id ?? '') : userDetails.id;
  const [carIds, setCarIds] = useState<string[]>(initialCarIds);
  const [assigneeId, setAssigneeId] = useState<string>(initialAssigneeId);
  const [name, setName] = useState(task?.title ?? '');
  const [quick, setQuick] = useState('');
  const [followUp, setFollowUp] = useState(task?.followUpDate ?? '');
  const [category, setCategory] = useState<TaskCategory>(task?.category ?? 'general');
  const [description, setDescription] = useState(task?.description ?? '');
  const [tidying, setTidying] = useState(false);
  const [tidyFailed, setTidyFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createFailed, setCreateFailed] = useState(false);

  const changes: PatchedTaskUpdateRequest = {};
  if (task) {
    if (name.trim() !== task.title) changes.title = name.trim();
    if (!sameIds(carIds, initialCarIds)) changes.carIds = carIds;
    if (assigneeId !== initialAssigneeId) changes.assigneeId = assigneeId;
    if ((followUp || null) !== task.followUpDate) changes.followUpDate = followUp || null;
    if (category !== task.category) changes.category = category;
    if (description.trim() !== task.description.trim()) changes.description = description.trim();
  }
  const changed = Object.keys(changes).length > 0;

  const canCreate =
    Boolean(name.trim()) &&
    !saving &&
    (task ? changed : Boolean(assigneeId) && Boolean(propertyId));
  const hasNotes = Boolean(description.trim());

  const tidy = async () => {
    if (!hasNotes || tidying) return;
    setTidying(true);
    setTidyFailed(false);
    try {
      setDescription(await tidyNotes(description));
    } catch {
      setTidyFailed(true);
    } finally {
      setTidying(false);
    }
  };

  const create = async () => {
    if (!canCreate) return;
    setSaving(true);
    setCreateFailed(false);
    if (task) {
      try {
        await props.onSave(changes);
        onClose();
      } catch {
        setCreateFailed(true);
        setSaving(false);
      }
      return;
    }
    try {
      const created = await createTask({
        propertyId,
        title: name.trim(),
        description: description.trim(),
        carIds,
        assigneeId,
        followUpDate: followUp || null,
      });
      props.onCreated(created);
    } catch {
      setCreateFailed(true);
      setSaving(false);
    }
  };

  let footerHint = propertyId ? 'Add a task name to create' : 'Pick a property to create';
  if (task) {
    footerHint = '';
    if (createFailed) footerHint = "Couldn't save the changes. Try again.";
    else if (!name.trim()) footerHint = 'Add a task name to save';
  } else if (createFailed) footerHint = "Couldn't create the task. Try again.";
  else if (name.trim() && assigneeId && propertyId)
    footerHint = `Created by you · ${formatDate(new Date().toISOString())}`;
  const titleId = task ? 'edit-task-title' : 'new-task-title';

  let tidyHint = hasNotes
    ? 'Rewrites into clear sentences — review before saving'
    : 'Jot something down first';
  if (tidyFailed) tidyHint = "Couldn't tidy the notes. Try again.";

  return (
    <>
      <DialogTitle
        id={titleId}
        sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', pr: 6 }}
      >
        {task ? 'Edit task' : 'New task'}
        {task ? null : (
          <Box component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary' }}>
            Quick capture — you can fill in the rest later.
          </Box>
        )}
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {fixedPropertyId ? (
          <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
            <Box component="b" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {portfolioName ?? '—'}
            </Box>
            {' · '}
            <Box component="b" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {propertyName}
            </Box>
            {task ? null : <> · set from the property you&apos;re on</>}
          </Box>
        ) : (
          <Autocomplete
            options={propertyOptions}
            value={pickedProperty}
            onChange={(_event, value) => {
              setPickedProperty(value);
              setCarIds([]);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Property" size="small" />}
          />
        )}

        <Box>
          <FieldLabel
            label="Elevators"
            hint={
              carIds.length ? undefined : 'None picked means the task covers the whole property'
            }
          />
          <Autocomplete
            multiple
            size="small"
            disabled={!propertyId}
            options={cars ?? []}
            value={(cars ?? []).filter((car) => carIds.includes(car.id))}
            onChange={(_event, value) => setCarIds(value.map((car) => car.id))}
            getOptionLabel={(car) => car.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            limitTags={4}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={carIds.length ? '' : 'Property-wide'}
                inputProps={{ ...params.inputProps, 'aria-label': 'Elevators' }}
              />
            )}
          />
        </Box>

        <Box>
          <FieldLabel label="Assign to" />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {people.map((person) => {
              const on = assigneeId === person.id;
              return (
                <Chip
                  key={person.id}
                  clickable
                  aria-pressed={on}
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: `${avatarColor(person.id)} !important`,
                        color: '#fff !important',
                        fontSize: '10px !important',
                        fontWeight: 700,
                      }}
                    >
                      {initials(person.name)}
                    </Avatar>
                  }
                  label={person.name}
                  onClick={() => setAssigneeId(person.id)}
                  sx={choiceSx(on)}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px' }}>
          <TextField
            id="new-task-name"
            label="Task name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Car 2 doors reopening, tech callback needed"
          />
          {task ? (
            <TextField
              select
              label="Category"
              size="small"
              value={category}
              onChange={(e) => setCategory(e.target.value as TaskCategory)}
              sx={{ minWidth: 200 }}
            >
              {(Object.keys(CATEGORY_LABEL) as TaskCategory[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {CATEGORY_LABEL[key]}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Box>

        <Box>
          <FieldLabel
            label="Next follow-up"
            hint="Optional. From this date the task shows in Needs Attention until you set a new date or clear it."
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {QUICK_FOLLOW_UPS.map(([label, days]) => {
              const on = quick === label;
              return (
                <Button
                  key={label}
                  size="small"
                  variant={on ? 'contained' : 'outlined'}
                  aria-pressed={on}
                  onClick={() => {
                    setQuick(on ? '' : label);
                    setFollowUp(on ? '' : isoDaysFromToday(days));
                  }}
                  sx={BUTTON_SX}
                >
                  {label}
                </Button>
              );
            })}
            <TextField
              type="date"
              size="small"
              value={followUp}
              onChange={(e) => {
                setFollowUp(e.target.value);
                setQuick('');
              }}
              slotProps={{ htmlInput: { 'aria-label': 'Follow-up date' } }}
            />
          </Box>
        </Box>

        <Box>
          <TextField
            label="Description"
            helperText="Type rough notes — tidy them up after"
            multiline
            minRows={task ? 2 : 3}
            maxRows={8}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Notes from the call or site walk…"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '8px' }}>
            <Button
              size="small"
              variant={hasNotes ? 'contained' : 'outlined'}
              startIcon={<AutoAwesomeIcon />}
              onClick={() => void tidy()}
              disabled={!hasNotes || tidying}
              sx={BUTTON_SX}
            >
              {tidying ? 'Tidying…' : 'Let AI tidy these notes'}
            </Button>
            <Box component="span" sx={hintSx}>
              {tidyHint}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box component="span" sx={{ ...hintSx, flex: 1 }}>
          {footerHint}
        </Box>
        <Button variant="outlined" onClick={onClose} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void create()}
          disabled={!canCreate}
          sx={BUTTON_SX}
        >
          {task ? 'Save changes' : 'Create task'}
        </Button>
      </DialogActions>
    </>
  );
}

export function NewTaskDialog({ open, ...rest }: Props) {
  return (
    <Dialog
      open={open}
      onClose={rest.onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={rest.task ? 'edit-task-title' : 'new-task-title'}
    >
      <NewTaskForm {...rest} />
    </Dialog>
  );
}
